/** The world: one grid per cell property, chunks, the seeded generator, compiled rules. Writes from outside step(). */
import * as Physique from './matrices/physique';
import * as CellState from './matrices/cell-state';
import {
  MATERIALS, EMPTY, FIRE, STATE_LIQUID, STATE_POWDER, STATE_STATIC, FLAG_INTERNAL, FLAG_FLAMMABLE, NO_CRUMBLE, NO_DECAY, DEFAULT_SPREAD,
  compileMaterials, type Material, type MaterialTables,
} from './materials';
import { REACTIONS, compileReactions, type CompiledReactions, type Reaction } from './reactions';
import { createChunkGrid, wakeRegion, type ChunkGrid } from './chunks';
import { createRng, nextU32, type Rng } from './prng';

export interface SimRules {
  readonly materials: MaterialTables;
  readonly reactions: CompiledReactions;
}

export function compileRules(materials: readonly Material[], reactions: readonly Reaction[]): SimRules {
  const compiled = compileMaterials(materials);
  return { materials: compiled, reactions: compileReactions(reactions, compiled.count) };
}

export const DEFAULT_RULES: SimRules = compileRules(MATERIALS, REACTIONS);

export interface World {
  physique: Physique.PhysiqueMatrix;
  /** Per-cell byte that moves with the cell: fall counter for powders, LIQ_* flags for liquids (see step()). */
  cellState: CellState.CellStateMatrix;
  chunks: ChunkGrid;
  /** Shared seeded generator: the only source of randomness in the simulation. */
  rng: Rng;
  rules: SimRules;
  tick: number;
  /** −10 to +10: pushes the gases sideways (fire spreading downwind). Set by the game each tick. */
  wind: number;
  /**
   * No atmosphere (the Moon, GDD section 10): fire goes out at once (black powder it touches still blows up)
   * and steam freezes into ice at once.
   */
  airless: boolean;
  /**
   * Explosions set off by reactions during the last step(), as flat [x, y, radius, ...]: the game turns
   * them into real explosions (damage, craters) and empties the list. Not part of the state between ticks.
   */
  blasts: number[];
  /** Scratch for step(), not part of the state: per column, the row stamp of the last liquid that flowed there. */
  movedInRow: Float64Array;
  /** Scratch for step(), not part of the state: per cell, tick + 1 when a gas moved into it this tick. */
  gasMoved: Int32Array;
}

export interface WorldOptions {
  seed?: number;
  rules?: SimRules;
  airless?: boolean;
}

export function createWorld(w: number, h: number, options: WorldOptions = {}): World {
  const world: World = {
    physique: Physique.createPhysique(w, h),
    cellState: CellState.createCellState(w, h),
    chunks: createChunkGrid(w, h),
    rng: createRng(options.seed ?? 1),
    rules: options.rules ?? DEFAULT_RULES,
    tick: 0,
    wind: 0,
    airless: options.airless ?? false,
    blasts: [],
    movedInRow: new Float64Array(w),
    gasMoved: new Int32Array(w * h),
  };
  wakeAll(world);
  return world;
}

/**
 * A powder grain only slides sideways after falling more than 3 cells, or when it sits on
 * more than 3 grains of its own material. It therefore looks up to SLIDE_DEPTH cells below itself,
 * so a change can affect the cells up to SLIDE_DEPTH rows above it.
 */
export const SLIDE_DEPTH = 4;
export const SLIDE_AFTER_FALL = 3;

/**
 * Liquids: a cell falls, else slides down diagonally, else flows up to LIQUID_SPREAD cells sideways
 * in its direction. Flowing liquid in the way makes it turn around if it has room behind. When it hits
 * an edge (a solid, the map border or resting liquid) it turns back once, and rests at the next edge:
 * water spreads flat and only piles up against edges. A resting cell still falls. It flows again toward
 * a row neighbour that leaves, or when it has liquid above it and room on a side (so a pool levels out
 * instead of keeping a wall).
 */
export const LIQUID_SPREAD = DEFAULT_SPREAD;
/** How far sideways a change can affect a liquid's decision (its path, plus the cell blocking it). */
export const LIQUID_REACH = LIQUID_SPREAD + 1;

/** Liquid flags, in the per-cell byte (`cellState`). 0 = fresh liquid, direction chosen on its first move. */
export const LIQ_HAS_DIR = 1;
export const LIQ_RIGHT = 2;
export const LIQ_TURNED = 4;
export const LIQ_RESTING = 8;

/** Wakes cells [x0..x1] × [y0..y1] plus every cell whose next move could depend on them. */
export function wakeRect(world: World, x0: number, y0: number, x1: number, y1: number): void {
  wakeRegion(world.chunks, world.physique.w, world.physique.h, x0 - LIQUID_REACH, y0 - SLIDE_DEPTH, x1 + LIQUID_REACH, y1 + 1);
}

/** Call after bulk raw writes (terrain generation, fills). */
export function wakeAll(world: World): void {
  wakeRegion(world.chunks, world.physique.w, world.physique.h, 0, 0, world.physique.w - 1, world.physique.h - 1);
}

/** The way to modify a single cell from outside the simulation: writes it and wakes its neighbourhood. */
export function setCell(world: World, x: number, y: number, mat: number): void {
  const { w, h } = world.physique;
  if (x < 0 || x >= w || y < 0 || y >= h) return;
  world.physique.data[y * w + x] = mat;
  world.cellState.data[y * w + x] = 0;
  wakeRect(world, x, y, x, y);
}

/** True for a material a tool may paint: known, and not engine-only. */
export function isPaintable(world: World, mat: number): boolean {
  const tables = world.rules.materials;
  return Number.isInteger(mat) && mat >= 0 && mat < tables.count && (tables.flags[mat] & FLAG_INTERNAL) === 0;
}

/** Fills a disc with `mat` (brushes, sandbox tools). Engine-only and unknown materials are ignored. */
export function paintDisc(world: World, cx: number, cy: number, radius: number, mat: number): void {
  if (!isPaintable(world, mat)) return;
  const { w, h, data } = world.physique;
  const r = radius < 0 ? 0 : radius;
  const minX = Math.max(0, cx - r);
  const maxX = Math.min(w - 1, cx + r);
  const minY = Math.max(0, cy - r);
  const maxY = Math.min(h - 1, cy + r);
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy > r * r) continue;
      data[y * w + x] = mat;
      world.cellState.data[y * w + x] = 0;
    }
  }
  if (minX <= maxX && minY <= maxY) wakeRect(world, minX, minY, maxX, maxY);
}

/** Fills the rectangle between two corners (inclusive, any order) with `mat`. Same rules as paintDisc. */
export function paintRect(world: World, x0: number, y0: number, x1: number, y1: number, mat: number): void {
  if (!isPaintable(world, mat)) return;
  const { w, h, data } = world.physique;
  const minX = Math.max(0, Math.min(x0, x1));
  const maxX = Math.min(w - 1, Math.max(x0, x1));
  const minY = Math.max(0, Math.min(y0, y1));
  const maxY = Math.min(h - 1, Math.max(y0, y1));
  if (minX > maxX || minY > maxY) return;
  for (let y = minY; y <= maxY; y++) {
    data.fill(mat, y * w + minX, y * w + maxX + 1);
    world.cellState.data.fill(0, y * w + minX, y * w + maxX + 1);
  }
  wakeRect(world, minX, minY, maxX, maxY);
}

/** Turns a cell into what its material crumbles into (rock -> sand). */
export function destroyCell(world: World, x: number, y: number): void {
  const mat = Physique.get(world.physique, x, y);
  const into = world.rules.materials.crumble[mat];
  if (into !== NO_CRUMBLE) setCell(world, x, y, into);
}

export function destroyCircle(world: World, cx: number, cy: number, radius: number): void {
  const { w, h, data } = world.physique;
  const crumble = world.rules.materials.crumble;
  const radiusSq = radius * radius;
  const minX = Math.max(0, cx - radius);
  const maxX = Math.min(w - 1, cx + radius);
  const minY = Math.max(0, cy - radius);
  const maxY = Math.min(h - 1, cy + radius);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const idx = y * w + x;
      if (dx * dx + dy * dy <= radiusSq && crumble[data[idx]] !== NO_CRUMBLE) {
        data[idx] = crumble[data[idx]];
        world.cellState.data[idx] = 0;
      }
    }
  }
  wakeRect(world, minX, minY, maxX, maxY);
}

/**
 * Empties a disc of `radius` (a cell with blastHits > 1 only loses one of its hits), and crumbles the cells
 * in the ring [radius, radius + rim] (rock -> sand).
 */
export function carveCrater(world: World, cx: number, cy: number, radius: number, rim: number): void {
  const w = world.physique.w;
  const h = world.physique.h;
  const phys = world.physique.data;
  const cells = world.cellState.data;
  const crumble = world.rules.materials.crumble;
  const blastHits = world.rules.materials.blastHits;
  const outer = radius + rim;
  const innerSq = radius * radius;
  const outerSq = outer * outer;
  const minX = Math.max(0, cx - outer);
  const maxX = Math.min(w - 1, cx + outer);
  const minY = Math.max(0, cy - outer);
  const maxY = Math.min(h - 1, cy + outer);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d2 = dx * dx + dy * dy;
      if (d2 > outerSq) continue;
      const idx = y * w + x;
      if (d2 <= innerSq) {
        // A tough cell (obsidian) counts its hits in its state byte and only goes at the last one.
        const hits = blastHits[phys[idx]];
        if (hits > 1 && cells[idx] + 1 < hits) {
          cells[idx]++;
          continue;
        }
        phys[idx] = EMPTY;
        cells[idx] = 0;
      } else if (crumble[phys[idx]] !== NO_CRUMBLE) {
        phys[idx] = crumble[phys[idx]];
        cells[idx] = 0;
      }
    }
  }
  if (minX <= maxX && minY <= maxY) wakeRect(world, minX, minY, maxX, maxY);
}

/**
 * Marks every liquid cell in [x0..x1] × [y0..y1] as resting, as if it had already spread and settled.
 * For world generation: a freshly filled sea is then still at once instead of sloshing for seconds.
 */
export function restLiquids(world: World, x0: number, y0: number, x1: number, y1: number): void {
  const { w, h, data } = world.physique;
  const cells = world.cellState.data;
  const state = world.rules.materials.state;
  for (let y = Math.max(0, y0); y <= Math.min(h - 1, y1); y++) {
    for (let x = Math.max(0, x0); x <= Math.min(w - 1, x1); x++) {
      const i = y * w + x;
      if (state[data[i]] === STATE_LIQUID) cells[i] = LIQ_HAS_DIR | ((x & 1) === 0 ? LIQ_RIGHT : 0) | LIQ_TURNED | LIQ_RESTING;
    }
  }
  wakeRect(world, x0, y0, x1, y1);
}

/** Width of the ring around an explosion where flammable cells catch fire. */
export const BLAST_RIM = 2;

/**
 * The crater of an explosion (GDD sections 8 and 12): every cell of the disc disappears, except water, which
 * boils into steam, and tough cells (obsidian), which only lose one of their hits. In the ring just outside,
 * what burns catches fire (black powder too, which then blows up), and one empty cell in four along the
 * edge of the crater starts a small fire: "enflamme les bords".
 */
export function blastCrater(world: World, cx: number, cy: number, radius: number): void {
  const { w, h, data } = world.physique;
  const cells = world.cellState.data;
  const tables = world.rules.materials;
  const outer = radius + BLAST_RIM;
  const innerSq = radius * radius;
  const outerSq = outer * outer;
  const minX = Math.max(0, cx - outer);
  const maxX = Math.min(w - 1, cx + outer);
  const minY = Math.max(0, cy - outer);
  const maxY = Math.min(h - 1, cy + outer);
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const d2 = (x - cx) * (x - cx) + (y - cy) * (y - cy);
      if (d2 > outerSq) continue;
      const i = y * w + x;
      const m = data[i];
      if (d2 <= innerSq) {
        const hits = tables.blastHits[m];
        if (hits > 1 && cells[i] + 1 < hits) { cells[i]++; continue; }
        data[i] = tables.boil[m] !== NO_DECAY ? tables.boil[m] : EMPTY;
        cells[i] = 0;
      } else if ((tables.flags[m] & FLAG_FLAMMABLE) !== 0) {
        data[i] = FIRE;
        cells[i] = 0;
      } else if (m === EMPTY && (nextU32(world.rng) & 3) === 0) {
        const below = y + 1 < h ? tables.state[data[i + w]] : STATE_STATIC;
        if (below === STATE_STATIC || below === STATE_POWDER) { data[i] = FIRE; cells[i] = 0; }
      }
    }
  }
  if (minX <= maxX && minY <= maxY) wakeRect(world, minX, minY, maxX, maxY);
}
